import type { FC } from 'react'
import type { Content } from '@prismicio/client'
import type { SliceComponentProps } from '@prismicio/react'
import { PrismicRichText } from '@prismicio/react'

// Explicit type for the group items
interface TitleCardItem {
	card_image?: { url?: string; alt?: string }
	card_title?: string
	card_description?: string
}

/**
 * Props for `SubsDetailTitleCards`.
 */
export type SubsDetailTitleCardsProps = SliceComponentProps<Content.SubsDetailTitleCardsSlice>

/**
 * Component for "SubsDetailTitleCards" Slices.
 */
// eslint-disable-next-line react/function-component-definition, arrow-body-style
const SubsDetailTitleCards: FC<SubsDetailTitleCardsProps> = ({ slice }) => {
	const { primary, items } = slice
	const sectionTitle = primary.section_title
	const sectionSubtitle = primary.section_subtitle as string | undefined
	const ctaText = primary.cta_text as string | undefined
	const ctaLink = primary.cta_link as string | undefined

	// Type assertion for items
	const cards = items as TitleCardItem[]

	return (
		<section
			data-slice-type={slice.slice_type}
			data-slice-variation={slice.variation}
			className="flex flex-col items-center font-sans px-6 mt-20 mb-20 max-w-7xl mx-auto"
		>
			<div className="font-serif text-4xl mb-6 text-center">
				{sectionTitle && <PrismicRichText field={sectionTitle} />}
			</div>
			{sectionSubtitle && <div className="text-xl max-w-3xl text-center">{sectionSubtitle}</div>}
			<div className="flex flex-col md:flex-row flex-wrap gap-8 md:gap-12 my-10 md:my-16 md:w-full">
				{cards?.map((item, idx) => {
					// Prefer a stable key: image URL + title, fallback to idx
					const key = item.card_image?.url
						? `${item.card_image.url}-${item.card_title || idx}`
						: item.card_title || idx
					return (
						<div
							key={key}
							className="flex flex-row md:flex-col items-center flex-1 gap-4 md:gap-8 md:mb-0"
						>
							{item.card_image?.url && (
								<div className="w-32 h-32 md:w-full max-w-[110px] md:max-w-[250px] md:h-full max-h-[110px] md:max-h-[250px] flex-shrink-0 rounded-full overflow-hidden bg-gray-100">
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src={item.card_image.url}
										alt={item.card_image.alt || ''}
										className="w-full h-full object-cover"
										width={128}
										height={128}
										loading="lazy"
									/>
								</div>
							)}
							<div className="flex flex-col text-left md:text-center">
								<div className="text-lg md:text-2xl text-bbx-style mb-2 font-normal">
									{item.card_title}
								</div>
								<div className="text-sm md:text-base text-bbx-style font-light opacity-80">
									{item.card_description}
								</div>
							</div>
						</div>
					)
				})}
			</div>
			{ctaText && ctaLink && (
				<a
					className="button button-dark-blue py-4 px-16 max-w-fit-content text-xl"
					href={ctaLink}
					type="button"
				>
					{ctaText}
				</a>
			)}
		</section>
	)
}

export default SubsDetailTitleCards
